import type { ContextHook, Hook, HookCtx, LayoutHook, MediaHook, PackedSheet } from "#internal"
import type { ImpositionLayout } from "#internal"

const apply_stage = (
  processors: Hook[],
  ctx: HookCtx,
  current: ImpositionLayout,
  select: (processor: Hook) => LayoutHook | undefined
): ImpositionLayout =>
  processors.reduce((acc, processor) => {
    const hook = select(processor)
    return hook ? hook(ctx, acc) : acc
  }, current)

/**
 * Mirrors Python `get_processors`/`make_composition` (concrete.py:469-488):
 * the per-sheet hook chain, `imposition_processor` then `sheet_job_processor`.
 * The inner-size and machine-size stages run inside the preset, on the grow
 * and on the fill, before the sheet exists.
 */
export const ApplyHooks = (
  processors: Hook[],
  ctx: HookCtx,
  current: ImpositionLayout
): ImpositionLayout =>
  apply_stage(
    processors,
    ctx,
    apply_stage(processors, ctx, current, p => p.imposition_processor),
    p => p.sheet_job_processor
  )

export const ApplyFinalHooks = (
  processors: Hook[],
  ctx: HookCtx,
  entries: PackedSheet[]
): PackedSheet[] =>
  processors.reduce<PackedSheet[]>(
    (acc, processor) =>
      processor.final_job_processor ? [...processor.final_job_processor(ctx, acc)] : acc,
    entries
  )

/** Every `media_processor` of the job as one: each takes what the one before
 * it answered, media by media. None leaves the media as it is. */
export const ApplyMediaHooks = (processors: Hook[]): MediaHook => {
  const hooks = processors.flatMap(p => (p.media_processor ? [p.media_processor] : []))
  return media => hooks.reduce((acc, hook) => acc.flatMap(m => [...hook(m)]), [media])
}

/** Every `context_processor` of the job as one, in order. None leaves the
 * context as it is. */
export const ApplyContextHooks = (processors: Hook[]): ContextHook => {
  const hooks = processors.flatMap(p => (p.context_processor ? [p.context_processor] : []))
  return ctx => hooks.reduce((acc, hook) => hook(acc), ctx)
}
